<template>
  <div class="orderPayment">
    <div v-if="order.hotel && order.hotel.paymentWays" id="payment-ways" class="payment__card">
      <div class="payment__block">
        <h4 class="payment__title">{{ $t('bid.form.cardTitle') }}</h4>
        <div class="payment__section flex flex-wrap w-full">
          <div class="hidden xl:block xl:w-2/3 pr-8">
            <div class="creditCard__display" :class="identifyCard">
              <div class="flex justify-between">
                <div class="chip"></div>
                <div v-if="creditCardType && creditCardType !== 'unknown'" class="cardTypeLogo uppercase">
                  <img v-if="cardLogo" :src="cardLogo" :alt="cardLogo">
                  <span v-else>{{ creditCardType }}</span>
                </div>
              </div>
              <div class="cardNumber uppercase mt-16">
                <label for="creditCardNumber">
                  <cleave class="w-full cursor-default" readonly v-model="creditCardNumber" :options="mask.creditCard.number"></cleave>
                </label>
              </div>
              <div class="flex flex-col justify-between">
                <div class="flex flex-col mb-16">
                  <label v-if="creditCardName" for="creditCardName" class="creditCard__display__label text-sm mb-4 mr-8">{{ $t('creditCardholder') }}</label>
                  <div class="creditCard__display__content">{{ creditCardName }}</div>
                </div>
                <div class="flex justify-between">
                  <div>
                    <label v-if="creditCardExpiryDate" for="creditCardExpiryDate" class="creditCard__display__label text-sm">{{ $t('validDate') }}</label>
                    <div><cleave readonly class="creditCard__display__content" v-model="creditCardExpiryDate" :options="mask.creditCard.expiryDate"></cleave></div>
                  </div>
                  <div class="ml-16">
                    <label v-if="creditCardCvc" for="creditCardCvc" class="creditCard__display__label text-sm">{{ $t('securityCode') }}</label>
                    <div><cleave readonly class="creditCard__display__content" v-model="creditCardCvc" :options="mask.creditCard.cvc"></cleave></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full xl:w-1/3 p-8 flex flex-col justify-between">
            <div class="payment__form-full">
              <img :src="paymentWayLogo('paynow-creditcard3ds')" :alt="'paynow-creditcard3ds'" :title="$t('paymentWay.paynow-creditcard3ds')">
            </div>
            <div class="payment__form-full">
              <label for="creditCardName" class="inline-block mb-8">{{ $t('creditCardholder') }}</label>
              <input v-model="creditCardName" type="text" class="payment__content input w-full rounded-full" id="creditCardName">
              <div class="errorMsg">{{ validation.firstError('creditCardName') }}</div>
            </div>
            <div class="payment__form-full">
              <label for="creditCardNumber" class="inline-block mb-8">{{ $t('cardNumber') }}</label>
              <cleave v-model="creditCardNumber" :options="mask.creditCard.number" class="info__content input w-full rounded-full" id="creditCardNumber"></cleave>
              <div class="errorMsg">{{ validation.firstError('creditCardNumber') }}</div>
            </div>
            <div class="payment__form-full flex">
              <div class="payment__form-half">
                <label for="creditCardExpiryDate" class="inline-block mb-8">{{ $t('validDate') }}</label>
                <cleave v-model="creditCardExpiryDate" :options="mask.creditCard.expiryDate" class="info__content input w-full rounded-full" id="creditCardExpiryDate" :placeholder="$t('cardPlaceholder.date')"></cleave>
                <div class="errorMsg">{{ validation.firstError('creditCardExpiryDate') }}</div>
              </div>
              <div class="payment__form-half">
                <label for="creditCardCvc" class="inline-block mb-8">{{ $t('securityCode') }}</label>
                <cleave v-model="creditCardCvc" :options="mask.creditCard.cvc" class="info__content input w-full rounded-full" id="creditCardCvc" :placeholder="$t('cardPlaceholder.cvc')"></cleave>
                <div class="errorMsg">{{ validation.firstError('creditCardCvc') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import orderMixin from '@/mixins/order'
import paymentMixin from '@/mixins/orderPayment/payment'
import { mapGetters, mapActions } from 'vuex'
import SimpleVueValidator from 'simple-vue-validator'
import { createHelpers } from 'vuex-map-fields'

const Validator = SimpleVueValidator.Validator
const { mapFields } = createHelpers({
  getterType: 'order/getField',
  mutationType: 'order/updateField'
})

const PAYNOW_PDF_LINK = {
  zh_TW: 'https://paynow-public.s3.ap-northeast-1.amazonaws.com/docs/%E6%9C%83%E5%93%A1%E6%A2%9D%E6%AC%BE%E8%88%87%E7%9B%B8%E9%97%9C%E6%9C%8D%E5%8B%99202300612.2306.pdf',
  others: 'https://paynow-public.s3.ap-northeast-1.amazonaws.com/docs/%E6%9C%83%E5%93%A1%E6%A2%9D%E6%AC%BE%E8%88%87%E7%9B%B8%E9%97%9C%E6%9C%8D%E5%8B%99202300612.2306.pdf'
}

export default {
  name: 'OrderPaymentBid',
  mixins: [
    orderMixin,
    paymentMixin
  ],
  metaInfo () {
    return {
      title: '訂單付款'
    }
  },
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isProcessing: false,
      mask: {
        creditCard: {
          number: {
            creditCard: true,
            creditCardStrictMode: false,
            onCreditCardTypeChanged: (type) => {
              this.creditCardType = type
            }
          },
          expiryDate: {
            date: true,
            datePattern: ['m', 'y']
          },
          cvc: {
            numericOnly: true,
            blocks: [3],
            maxLength: 4
          }
        }
      },
      paynowCheck: false
    }
  },
  filters: {
    toPercentage: function (val) {
      return 100 - val * 100
    }
  },
  validators: {
    'creditCardName': function (val) {
      return Validator.value(val).required().minLength(2).regex(/^([^0-9]*)$/)
    },
    'creditCardNumber': function (val) {
      return Validator.value(val).required().length(16)
    },
    'creditCardExpiryDate': function (val) {
      return Validator.value(val).required().custom(() => {
        if (val && val.length !== 4) {
          return this.$t('validatorErrors.expiryDateFormat')
        } else {
          const isAfter = this.$dayjs(val, 'MMYY').isSameOrAfter(this.$dayjs(), 'month')
          return isAfter ? null : this.$t('validatorErrors.expiryDateCantBeforeToday')
        }
      })
    },
    'creditCardCvc': function (val) {
      return Validator.value(val).required().length(3)
    }
  },
  computed: {
    ...mapFields({
      payMethod: 'payment.payMethod',
      creditCardType: 'payment.creditCard.type',
      creditCardName: 'payment.creditCard.name',
      creditCardNumber: 'payment.creditCard.number',
      creditCardExpiryDate: 'payment.creditCard.expiryDate',
      creditCardCvc: 'payment.creditCard.cvc'
    }),
    ...mapGetters([
      'user/currencySign',
      'isSuper',
      'user/selectedLang'
    ]),
    isNotAllowToPay: function () {
      return !this.payMethod || this.isTimerEnded || this.isProcessing || (this.isPaynow && !this.paynowCheck)
    },
    currencySign: function () {
      return this['user/currencySign']
    },
    paymentWays: function () {
      return this.order.hotel.paymentWays
    },
    identifyCard: function () {
      let classList = []
      if (this.creditCardType && this.creditCardType !== 'unknown') {
        classList.push(this.creditCardType)
        classList.push('isIdentify')
      }
      return classList
    },
    cardLogo: function () {
      try {
        require(`@/assets/creditcard/logo-${this.creditCardType}.svg`)
      } catch (err) {
        return false
      }
      return require(`@/assets/creditcard/logo-${this.creditCardType}.svg`)
    },
    isPaynow: function () {
      return this.payMethod?.startsWith('paynow')
    },
    paynowPdfLink: function () {
      return this['user/selectedLang'] === 'zh_TW' ? PAYNOW_PDF_LINK['zh_TW'] : PAYNOW_PDF_LINK['others']
    }
  },
  methods: {
    ...mapActions([
    ]),
    onClickGoToPay: function () {
      this.isProcessing = true
      this.$validate().then(success => {
        if (success) {
          this.createPayment(this.order)
        } else {
          this.isProcessing = false
        }
      })
    },
    switchProcessingStatus: function (status) {
      this.isProcessing = status
    },
    // Credit Card
    onCardValidate: function () {
      return new Promise((resolve, reject) => {
        this.$validate().then(success => {
          if (success) {
            resolve()
          } else {
            reject(new Error('card error: ', this.validation))
          }
        })
      })
    },
    clearCreditCardInfo: function () {
      this.creditCardNumber = null
      this.creditCardName = null
      this.creditCardExpiryDate = null
      this.creditCardCvc = null
    }
  },
  mounted () {
    this.payMethod = 'paynow-creditcard3ds'
  },
  created () {
  },
  watch: {
  },
  beforeDestroy () {
    this.clearCreditCardInfo()
  }
}
</script>

<style lang="sass" scoped>
  .orderPayment
    @apply mt-16
  .payment__card
    @apply border border-grey-lighter
    @apply mb-24
    border-radius: 20px

  .payment__coupon-content
    figure
      width: 80px
      height: 80px
      @screen md
        width: 100px
        height: 100px
      img
        @apply w-full

  .payment__coupon-copy
    @apply flex-no-shrink flex flex-col justify-between relative
    &::before
      content: ''
      width: calc(100% - 32px)
      top: 0
      @apply border-t-1 border-grey-lighter mx-auto absolute
    @screen md
      @apply border-l-1 border-grey-lighter
      &::before
        @apply hidden
    &__btns
      @apply w-full mt-16
      button
        @apply w-full
      @screen md
        @apply w-auto mt-0
        button
          @apply w-auto

  .payment__form-half
    @apply w-1/2
    @apply pr-8 pl-0
    & + &
      @apply pr-0 pl-8
  .payment__form-full
    @apply w-full
    & + &
      @apply mt-24

  .payment__block
    @apply p-16
    @screen sm
      @apply p-24
    & + &
      @apply border-t-1 border-grey-lighter
  .payment__block-head
    @apply flex justify-between items-center
  .payment__owl-points
    @apply text-2xl text-blue
  .payment__title
    @apply font-bold
  .payment__section
    @apply mt-24
  .payment__switcher
    @apply flex
    &-radio
      & + &
        @apply mt-16
        @screen sm
          @apply mt-0 ml-32

  .change-coupon-btn
    @apply py-0

  .coupon-text-block
    order: 2
    @screen md
      order: 1

  .coupon-btn-block
    order: 1
    @screen md
      order: 2

  .creditCard__display
    @apply shadow px-16 py-24 flex flex-col justify-between
    background: backgroundGradient(linear, 180deg, #F2F4F6, #fff)
    max-width: 500px
    min-height: 280px
    transition: background 3s ease
    border-radius: 20px
    .chip
      @apply rounded
      background:
        image: url('~@/assets/creditcard/img-chip.svg')
        repeat: no-repeat
      width: 45px
      height: 40px
    .cardNumber
      @apply text-xl font-bold leading-loose
      letter-spacing: 0.2em
    &__label
      @apply text-grey
    &__content
      @apply font-bold
    &.isIdentify
      background-image: linear-gradient(320deg, #3c4145, #889096)
      @apply text-white #{!important}
      label, input
        @apply text-white #{!important}
    &.mastercard
      background: backgroundGradient(linear, 320deg, #3c4145, #889096)
    &.visa
      background: backgroundGradient(linear, 127deg, #8dd8f5, #07689f)
    &.unionPay
      background: backgroundGradient(linear, 320deg, #085987, #38cccc)
    &.jcb
      background: backgroundGradient(linear, 127deg, #ffb6a8, #d23a07)
    &.cirrus
      background: backgroundGradient(linear, 127deg, #9a5ddb, #103d57)
    &.amex
      background: backgroundGradient(linear, 320deg, #07689f, #d1d7dd)
    &.discover
      background: backgroundGradient(linear, 320deg, #f7ac15, #e07612)
    &.diners
      background: backgroundGradient(linear, 320deg, #078abc, #103d57)

  .errorMsg
    @apply text-xs text-red ml-4 mt-8
</style>
